import React, { useState } from "react";

import { faPen, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Row, Alert, Button, Col, Input, Label } from "reactstrap";
import { awsApi } from "../../../../../services/awsService";
import InformationModal from "../../../../InformationModal";
import Loader from "../../../../Loader";
import { workOrdersApi } from "../../../../../services/workOrdersServices";
import { v1 as uuidv1 } from "uuid";
import { utils } from "../../../../../utils/utils";
import {
  useWorkOrderDetails,
  ACTIONS,
} from "../../../../../providers/workOrderDetailsProvider";
import ModalEdit from "./ModalEdit";
import { cranesInspectionAttachmentApi } from "../../../../../services/craneInspectionAttachmentServices";

const TYPE_IMAGE = "IMAGE";

const EditAttachmentsModal = ({
  attachments,
  employeeWorkDayId,
  isCraneInspected = false,
  craneInspectionId = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState(attachments);
  const [errors, setErrors] = useState();
  const [deletedPhotos, setDeletedPhotos] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [, setWorkOrderDetails] = useWorkOrderDetails();

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  const onClose = () => {
    setOpenModal(false);
  };

  const onEditCaption = async (photos) => {
    const photosToUpdate = photos.filter((photo) => photo.changedLabel);
    const updatePromises = photosToUpdate.map((photo) => {
      if (isCraneInspected) {
        const postData = {
          id: photo.id,
          craneInspectionId: craneInspectionId,
          content: photo.content,
          label: photo.label,
          type: TYPE_IMAGE,
        };
        return cranesInspectionAttachmentApi.updateCraneInspectionAttachment(
          postData
        );
      } else {
        const postData = {
          employeeWorkDayId: employeeWorkDayId[0].id,
          label: photo.label,
          id: photo.id,
          contentUrl: photo.contentUrl,
          workOrderCraneId:
            attachments.find((item) => item.workOrderCraneId)
              ?.workOrderCraneId || null,
        };
        return workOrdersApi.updateGeneralServiceAttachments(postData);
      }
    });
    await Promise.all(updatePromises);
  };

  const onSubmit = async () => {
    setLoading(true);
    const upload = [
      ...photos
        .map((item, i) => ({
          ...item,
          fileName: `general_service_attachment_${uuidv1()}_${i}`,
        }))
        .filter((img) => !img.id),
    ];

    const documents = upload.map((item) => ({
      ...item,
      method: "putObject",
    }));

    const promise = documents.length
      ? awsApi.signDocuments({ documents })
      : Promise.resolve();

    const signedURLs = await promise;
    const elements = (signedURLs || []).filter((sign) => sign.success);

    if (!elements.length) {
      onEditCaption(photos);
    } else {
      const imagesCreated = photos.filter((photo) =>
        attachments.some((attachment) => attachment.id === photo.id)
      );
      onEditCaption(imagesCreated);
    }

    let promises = elements
      .filter((element) =>
        upload.find((file) => file.fileName === element.fileName)
      )
      .map(async (element) => {
        const fileToUpload = upload.find(
          (file) => file.fileName === element.fileName
        );

        const file = await utils.srcToFile(
          isCraneInspected ? fileToUpload.content : fileToUpload.contentUrl,
          fileToUpload.fileName,
          fileToUpload.fileType
        );
        const data = {
          url: element.signedRequest,
          file: file,
          type: fileToUpload.fileType,
        };
        await awsApi.putDocumentsToS3(data);
        if (isCraneInspected) {
          const postData = {
            craneInspectionId: craneInspectionId,
            content: element.url,
            label: fileToUpload.label,
            type: TYPE_IMAGE,
          };

          return cranesInspectionAttachmentApi.createCraneInspectionAttachment(
            postData
          );
        } else {
          const postData = {
            employeeWorkDayId: employeeWorkDayId[0].id,
            contentUrl: element.url,
            label: fileToUpload.label,
            workOrderCraneId:
              attachments.find((item) => item.workOrderCraneId)
                ?.workOrderCraneId || null,
          };

          return workOrdersApi.createGeneralServiceAttachments(postData);
        }
      });

    if (deletedPhotos.length > 0) {
      if (isCraneInspected) {
        promises = [
          ...promises,
          ...deletedPhotos.map((photo) =>
            cranesInspectionAttachmentApi.deletCraneInspectionAttachment(
              photo.id
            )
          ),
        ];
      } else {
        promises = [
          ...promises,
          ...deletedPhotos.map((photo) =>
            workOrdersApi.deleteGeneralServiceAttachments(photo.id)
          ),
        ];
      }
    }

    try {
      await Promise.all(promises);
      setLoading(false);
      setOpenModal(false);
      setInformationModal({
        isOpen: true,
        title: "Update attachments",
        body: `The attached files were updated`,
        onClose: () => {
          setLoading(false);
          setWorkOrderDetails({
            action: ACTIONS.REFRESH,
          });
        },
      });
    } catch (err) {
      setLoading(false);
      if (err.isAxiosError) {
        setErrors(err.response.data);
        return;
      }
      setInformationModal({
        isOpen: true,
        title: "Error",
        body: "There was an error with your request.",
      });
    }
  };

  const onDelete = (photo) => {
    setPhotos(photos.filter((value) => photo !== value));
    if (!photo.id) {
      return;
    }
    setDeletedPhotos([...deletedPhotos, photo]);
  };

  const onUpdateCaptionPhotos = (e, index) => {
    const updatedPhotosCaption = [...photos];
    updatedPhotosCaption[index].label = e.target.value;
    updatedPhotosCaption[index].changedLabel = true;
    setPhotos(updatedPhotosCaption);
  };

  return (
    <>
      <Button
        size="sm"
        className="rounded ml-2"
        color="warning"
        onClick={() => setOpenModal(true)}
      >
        <FontAwesomeIcon icon={faPen} className="mr-1" />
        <span>Edit</span>
      </Button>
      {informationModal.isOpen ? (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          onClose={() =>
            informationModal.onClose
              ? informationModal.onClose()
              : setInformationModal({
                  isOpen: false,
                  title: "",
                  body: "",
                })
          }
        />
      ) : (
        <ModalEdit
          isOpen={openModal}
          onToggle={() => setOpenModal(false)}
          title="Edit Attachments"
          onCancel={onClose}
          onSave={onSubmit}
          isSaveDisabled={loading || (!photos.length && !deletedPhotos.length)}
        >
          {errors && (
            <Alert color="dark">
              <div className="p-2">
                {(errors || []).map((error) => (
                  <div key={error.param}>
                    - {error.param}: {error.msg}
                  </div>
                ))}
              </div>
            </Alert>
          )}
          <Row>
            {loading ? (
              <Loader size="sm" />
            ) : (
              <>
                <Col>
                  <Label for="Photos">
                    <span>Add Photos</span>
                  </Label>

                  <Input
                    required={false}
                    multiple={true}
                    type="file"
                    accept="image/gif, image/jpeg, image/png, image/jpg"
                    className="mb-1 text-truncate"
                    onChange={(event) => {
                      setPhotos([
                        ...photos,
                        ...[...event.currentTarget.files].map((file) => ({
                          fileName: file.name,
                          fileType: file.type,
                          ...(isCraneInspected
                            ? { content: URL.createObjectURL(file) }
                            : { contentUrl: URL.createObjectURL(file) }),
                        })),
                      ]);
                    }}
                  />
                </Col>
                <div className="d-flex flex-wrap">
                  {photos?.length
                    ? photos.map((photo, i) => (
                        <div className="col-3" key={i}>
                          <Button
                            className="close"
                            color="none"
                            onClick={() => onDelete(photo)}
                          >
                            <FontAwesomeIcon icon={faTimesCircle} size="xs" />
                          </Button>
                          <img
                            alt=""
                            className="col-12 px-0 rounded photo-crane"
                            src={
                              isCraneInspected
                                ? photo.content
                                : photo.contentUrl
                            }
                          />
                          <Input
                            type="textarea"
                            bsSize="sm"
                            placeholder="Enter the caption"
                            required={false}
                            maxLength={50}
                            className="mt-1"
                            value={photo.label || ""}
                            style={{ maxHeight: "70px" }}
                            onChange={(e) => onUpdateCaptionPhotos(e, i)}
                          />
                        </div>
                      ))
                    : null}
                </div>
              </>
            )}
          </Row>
        </ModalEdit>
      )}
    </>
  );
};

export default EditAttachmentsModal;
